import { useState } from 'react';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';

import { MPRModel, type DCCTVolumeStatus } from '@/features/MPR';

import type { FullHeadMprAvailability } from '../config';

type UseFullHeadMprAvailabilityParams = {
  patientID: string;
  reportID: string;
};

type ResolvedAvailability = Exclude<FullHeadMprAvailability, 'pending'>;

type LastResolvedAvailability = {
  patientID: string;
  reportID: string;
  value: ResolvedAvailability;
};

const resolveAvailability = (
  volumeStatus: DCCTVolumeStatus,
): ResolvedAvailability | null => {
  if (volumeStatus === 'ready') {
    return 'available';
  }

  if (volumeStatus === 'missing' || volumeStatus === 'failed') {
    return 'unavailable';
  }

  return null;
};

export const useFullHeadMprAvailability = (
  params: UseFullHeadMprAvailabilityParams,
): FullHeadMprAvailability => {
  const { patientID, reportID } = params;

  const isFullHeadMprEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_FullHeadMPRs,
    ),
  );
  const dcctVolumeStatus = useAppSelector(
    MPRModel.selectors.selectDCCTVolumeStatus({ patientID, reportID }),
  );

  const [lastResolved, setLastResolved] =
    useState<LastResolvedAvailability | null>(null);

  const resolved = resolveAvailability(dcctVolumeStatus);
  const isLastResolvedActual =
    lastResolved !== null &&
    lastResolved.patientID === patientID &&
    lastResolved.reportID === reportID;

  if (
    resolved !== null &&
    (!isLastResolvedActual || lastResolved.value !== resolved)
  ) {
    setLastResolved({ patientID, reportID, value: resolved });
  }

  if (!isFullHeadMprEnabled) {
    return 'unavailable';
  }

  if (resolved !== null) {
    return resolved;
  }

  return isLastResolvedActual ? lastResolved.value : 'pending';
};
